import { styled, css } from "styled-components";
import theme from "styled-theming";

import Button from "components/Button/Button";
import Icon from "components/Icon/Icon";
import TextBlock from "components/TextBlock/TextBlock";
import { Container as GridContainer } from "style/components/Page";
import {
    styleBodyLAlt,
    styleHeadingL,
    styleHeadingXL,
    styleHeadingXXL,
    styleBodyM,
    styleHeadingM,
    styleBodyS,
    HeadingS,
    styleNumber,
} from "style/components/Typography";
import { columnGap } from "style/grid";
import { MQ } from "style/mediaQueries";

const backgroundColor = theme("theme", {
    undefined: "--color-white",
    lightgray: "--color-white",
    blue: "--color-cyan-o20",
});

const labelErrorColor = theme("theme", {
    undefined: "--label-on-neutral-error",
    lightgray: "--label-on-neutral-error",
    blue: "--label-on-blue-error",
});

export const Container = styled(GridContainer)`
    ${columnGap}
    display: grid;
    grid-template-columns: repeat(12, [col-start] 1fr);
    grid-template-rows: auto 1fr;
    padding-left: 0;
    padding-right: 0;
    width: 100%;

    ${MQ.FROM_L} {
        padding: 3.5rem 2.1875rem 6.125rem;
    }

    ${MQ.FROM_XL} {
        padding: 4.375rem 3.5rem 8.75rem;
    }
`;

export const Header = styled.div`
    grid-column: col-start 1 / span 12;
    ${MQ.FROM_L} {
        grid-column: 1 col-start / span 6;
    }
`;

export const Heading = styled.h2`
    ${styleHeadingXL};
    margin-top: 0;
    padding: 0 2.1875rem;

    ${MQ.FROM_L} {
        padding: 0;
    }
`;

export const Calculator = styled.div`
    grid-column: col-start 1 / span 12;

    ${MQ.FROM_L} {
        grid-column: col-start 7 / span 5;
    }
`;

export const ResultItem = styled.div`
    grid-column: col-start 1 / span 12;

    ${MQ.FROM_L} {
        grid-column: col-start 7 / span 5;
    }
`;

export const SubHeading = styled.h2`
    ${styleHeadingM};
`;

export const Introduction = styled.p`
    ${styleBodyLAlt};
    padding: 0 2.1875rem;

    ${MQ.FROM_L} {
        padding: 0;
    }
`;

export const FieldWrapper = styled.div`
    margin-top: 1.75rem;
    padding: 0 2.1875rem;

    &:first-child {
        margin-top: 0;
    }

    ${MQ.FROM_L} {
        padding: 0;
    }
`;

export const CheckIcon = styled(Icon)`
    color: var(--icon-on-neutral-primary-alt1);
`;

export const ResultInnerTotal = styled.div`
    background-color: var(--block-cyan-default);
    margin-top: 3rem;
    padding: 1.75rem;
`;

export const ResultInner = styled.div`
    background-color: var(${backgroundColor});
    padding: 1.75rem;
`;

export const ResultInnerBottom = styled.div`
    padding: 0 1.75rem;

    ${MQ.FROM_L} {
        padding: 0;
    }
`;

export const ResultLabel = styled.h2`
    ${HeadingS};
`;

export const ResultText = styled.p`
    ${styleBodyM};
    margin: 1.75rem auto 0;
`;

export const ResultContent = styled(TextBlock)`
    margin-top: 1.75rem;
`;

export const NotificationWrapper = styled.div`
    padding: 0 2.1875rem;

    ${MQ.FROM_L} {
        padding: 0;
    }
`;

export const NotificationText = styled.p`
    ${styleBodyS}
    ${theme("error", {
        true: css`
            color: var(${labelErrorColor});
            font-weight: 500;
        `,
    })}
`;

export const ShowResultButton = styled(Button)`
    margin-top: 1.75rem;
`;

export const MonthlySavingAmount = styled.span`
    ${styleHeadingM};
    ${styleNumber};
    ${theme("theme", {
        lightgray: css`
            color: var(--text-on-cyan-primary);
        `,
        undefined: css`
            color: var(--text-on-cyan-primary);
        `,
        blue: css`
            color: var(--text-on-neutral-inverted);
        `,
    })};
    display: block;
    margin-top: 0.625rem;
`;

export const CalculationDetails = styled.details`
    margin: 1.75rem auto 0;

    &[open] {
        summary > svg {
            transform: rotate(180deg);
        }
    }
`;

export const CalculationSummary = styled.summary`
    ${styleBodyM}
    border-bottom: none;
    color: inherit;
    cursor: pointer;
    display: inline-flex;
    flex-wrap: nowrap;

    &:focus-visible {
        outline: 7px solid var(--button-focus);
        outline-offset: 7px;
    }

    svg {
        margin-left: 0.875rem;
        transition: transform ease-in-out 0.2s;
    }
`;

export const ShowCalculationButtonText = styled.div`
    > span {
        border-bottom: 1px solid;
    }
`;

export const TotalSavingAmount = styled.span`
    ${styleHeadingXXL};
    ${styleNumber};
    display: block;
    margin-top: 0.625rem;

    ${MQ.FROM_M} {
        ${styleHeadingXL};
    }

    ${MQ.FROM_L} {
        ${styleHeadingL};
    }
`;

export const TotalSavingAmountDescription = styled.span`
    ${styleBodyM};
    display: block;
    margin-top: 0.625rem;
`;
